import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { createRoot } from 'react-dom/client';
import { AnalyticsIdentity } from './app/AnalyticsIdentity';
import { OrgProvider } from './app/OrgProvider';
import { AuthProvider } from './auth/AuthProvider';
import { initAmplitude } from './lib/amplitude';
import { initAnalytics } from './lib/analytics';
import { ApiError } from './lib/api';
import { canonicalUrl, onCustomDomain, servedOnCustomDomain } from './lib/env';
import { ThemeProvider } from './lib/theme';
import { router } from './router';
import './styles.css';

/**
 * Note the absence of <StrictMode>.
 *
 * StrictMode double-invokes effects in development, which for an ordinary app
 * is a useful bug detector and for this one is a hazard: the recorder acquires
 * MediaStreams, MediaRecorders and AudioContexts in effects, and tearing those
 * down and rebuilding them behind the user's back means a second screen-share
 * prompt and a dead capture. The recorder's own lifecycle is written to be
 * idempotent; the development-only double-mount is not worth the noise.
 */

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Retrying a 401/403/404 just delays the real message.
        if (error instanceof ApiError && error.status < 500 && error.status !== 429) return false;
        return failureCount < 2;
      },
    },
    mutations: { retry: false },
  },
});

/**
 * A team's own domain serves share pages and nothing else.
 *
 * It is the same bundle, so every route works there by default, which would
 * put a Slate sign-in page on a customer's domain that Google refuses to
 * authorise and that would mint a second session if it did. Anything that is
 * not a viewer's page goes back to the one origin the app lives at, before
 * React mounts and before Firebase initialises, so there is no flash of an
 * app nobody should be seeing there.
 */
const misplaced = onCustomDomain() && !servedOnCustomDomain(window.location.pathname);
if (misplaced) {
  const { pathname, search, hash } = window.location;
  window.location.replace(canonicalUrl(`${pathname}${search}${hash}`));
}

if (!misplaced) {
  initAnalytics();
  initAmplitude();

  const container = document.getElementById('root');
  if (!container) throw new Error('index.html is missing its #root element.');

  createRoot(container).render(
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <OrgProvider>
            <AnalyticsIdentity />
            <RouterProvider router={router} />
          </OrgProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>,
  );
}
